import { ComponentProps, FC, MouseEventHandler } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import { NOT_APPLICABLE, WaterfallCellValue } from './sample-waterfall-column-helpers';

type CellValueProps = ComponentProps<typeof CellValue>;

interface WaterfallValueCellProps
  extends Omit<CellValueProps, 'value' | 'className' | 'onClick' | 'alignment'> {
  value: WaterfallCellValue;
  /** Applied to the cell itself; use valueClassName to style the rendered value. */
  className?: string;
  valueClassName?: string;
  /** Formatting rules passed through to CellValue, defaulting to the standard amount rules. */
  rules?: Partial<Omit<CellValueProps, 'value'>>;
  onClick?: MouseEventHandler<HTMLTableCellElement>;
}

/**
 * Renders a single scenario (column) cell. Rows are rendered for every scenario in the table,
 * so a scenario that does not require the row is highlighted as not applicable rather than
 * being left as an ordinary empty cell.
 */
const WaterfallValueCell: FC<WaterfallValueCellProps> = ({
  value,
  className,
  valueClassName,
  rules = amountRules,
  onClick,
  ...cellValueProps
}) => {
  if (value === NOT_APPLICABLE) {
    return <td className={classNames(className, styles['not-applicable-cell'])} />;
  }

  if (value === undefined || value === null) {
    return <td className={className} />;
  }

  return (
    <td className={className} onClick={onClick}>
      <CellValue
        value={value}
        alignment={Alignment.Right}
        className={valueClassName}
        {...rules}
        {...cellValueProps}
      />
    </td>
  );
};

export default WaterfallValueCell;
